import { TranslationContext } from "@clockwise/client-commons/src/providers/TranslationContext";
import { Switch } from "@clockwise/design-system";
import { Tooltip } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import classNames from "classnames";
import { noop } from "lodash";
import { CalendarBlank, Sparkle } from "phosphor-react";
import React from "react";
import { AttendeeAvatar } from "../../ui/AttendeeAvatar";
import { AttendeeAvatarStack } from "../../ui/AttendeeAvatarStack";
import { Profile } from "../../util/profile.util";

type Props = {
  attendees?: Profile[];
  editable?: boolean;
  eventId?: string;
  flexible?: boolean;
  loading?: boolean;
  nextOccruance?: string;
  onFlexibilityChange?: (eventId: string, isFlexible: boolean) => void;
  recurrence?: string;
  showAvatars?: boolean;
  title?: string;
  updating?: boolean;
};

const EventTableRow = ({
  attendees = [],
  editable = true,
  eventId = "",
  flexible = false,
  loading = false,
  nextOccruance = "",
  onFlexibilityChange = noop,
  recurrence = "",
  showAvatars = false,
  title = "Untitled Event",
  updating = false,
}: Props) => {
  const translation = React.useContext(TranslationContext);
  const tooltipText = translation("autopilot-form:toggle-tooltip", { on: !!flexible });

  const handleFlexibilityChange = (isFlexible: boolean) => onFlexibilityChange(eventId, isFlexible);

  let icon = <Sparkle size={23} />;
  if (!flexible) icon = <CalendarBlank size={23} />;

  return (
    <div className="cw-body-sm cw-flex cw-gap-1 cw-items-center cw-w-full cw-py-1" role="row">
      <div className="cw-flex cw-flex-grow cw-flex-wrap cw-items-center">
        <div className="cw-flex cw-items-center cw-flex-nowrap cw-gap-1">
          <div>
            {loading ? (
              <Skeleton variant="circle" width="20px" height="20px" className="cw-mr-1 cw-my-1" />
            ) : (
              <div className="cw-flex cw-items-center">{icon}</div>
            )}
          </div>
          <div>
            <div className="cw-w-max cw-flex cw-flex-wrap cw-items-center cw-font-semibold">
              {loading ? (
                <div className="cw-flex cw-items-top">
                  <Skeleton variant="text" width="10rem" />
                  {showAvatars && (
                    <div className="cw-flex cw-pl-1 cw-pr-2 cw-space-x-1">
                      <Skeleton variant="circle" width="20px" height="20px" />
                      <Skeleton variant="circle" width="20px" height="20px" />
                      <Skeleton variant="circle" width="20px" height="20px" />
                    </div>
                  )}
                </div>
              ) : (
                <div className="cw-w-max cw-flex cw-flex-wrap">
                  <div className="cw-max-w-[8rem] sm:cw-max-w-[12rem] md:cw-max-w-[16rem] lg:cw-max-w-[20rem] cw-truncate">
                    <div className="cw-text-accent cw-font-semibold">{title}</div>
                  </div>
                  {showAvatars && (
                    <div className="cw-pl-1 cw-pr-2">
                      <>
                        {attendees && (
                          <AttendeeAvatarStack size="small" overlap maxShown={3}>
                            {attendees.map((attendee, index) => {
                              return <AttendeeAvatar key={index} profile={attendee} size="small" />;
                            })}
                          </AttendeeAvatarStack>
                        )}
                        {!attendees && <div>meeting has no attendees</div>}
                      </>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="cw-flex-grow">
          <div
            className={classNames("cw-flex cw-items-center cw-flex-wrap", {
              "cw-text-default": !flexible,
              "cw-text-positive": flexible,
            })}
          >
            {(loading || nextOccruance) && (
              <div className="cw-font-medium cw-mim-w-max cw-mr-3">
                {loading ? <Skeleton variant="text" width="10rem" /> : nextOccruance}
              </div>
            )}
            {(loading || recurrence) && (
              <div className="cw-font-light">
                {loading ? <Skeleton variant="text" width="10rem" /> : recurrence}
              </div>
            )}
          </div>
        </div>
      </div>
      {editable && (
        <div className="cw-flex-shrink-0 cw-ml-2">
          <label className="cw-space-x-3 cw-flex cw-flex-nowrap cw-items-center cw-justify-end">
            {loading && <Skeleton variant="text" width="4rem" />}
            {!loading && flexible && (
              <span className="cw-font-medium cw-text-positive cw-text-end">Flexible</span>
            )}
            {!loading && !flexible && (
              <span className="cw-font-light cw-text-end cw-whitespace-wrap md:cw-whitespace-nowrap">
                Mark as flexible
              </span>
            )}
            <Tooltip title={tooltipText}>
              <Switch
                checked={flexible}
                disabled={loading || updating}
                onChange={handleFlexibilityChange}
                size="small"
              />
            </Tooltip>
          </label>
        </div>
      )}
    </div>
  );
};

export default EventTableRow;
